<template>
  <div class="research">
    <h1 v-bg-typography="'h1'" :style="{ marginTop: '10px' }">Test setup</h1>
    <div class="testsetup-tabs">
      <h2
        :class="this.activeTab === 'setup' ? 'active' : ''"
        @click="changeTab('setup')"
      >
        Setup
      </h2>
      <h2
        :class="this.activeTab === 'methods' ? 'active' : ''"
        @click="changeTab('methods')"
      >
        Methods
      </h2>
      <h2
        :class="this.activeTab === 'participants' ? 'active' : ''"
        @click="changeTab('participants')"
      >
        Participants
      </h2>
      <h2
        :class="this.activeTab === 'insights' ? 'active' : ''"
        @click="changeTab('insights')"
      >
        Insights
      </h2>
    </div>

    <div
      class="testsetup"
      v-if="this.activeTab === 'setup' && !isLoading"
      :key="this.rerenderKey"
    >
      <bg-field class="field" label="Test name">
        <bg-input v-model="test.name" height="46px" />
      </bg-field>
      <bg-field class="field" label="Company">
        <bg-select
          v-if="!$store.state.companies.loading"
          @change="selectCompany"
          :options="$store.state.companies.companies"
          :value="test.company"
          label="name"
        ></bg-select>
        <div v-else style="height: 46px">
          <div class="lds-ellipsis">
            <div></div>
            <div></div>
            <div></div>
            <div></div>
          </div>
        </div>
      </bg-field>

      <bg-field class="field" label="Status">
        <bg-select
          @change="selectStatus"
          :options="statusOptions"
          :value="test.state"
          label="text"
        ></bg-select>
      </bg-field>
      <bg-field class="field" label="Scenario">
        <bg-input v-model="test.scenario" tag="textarea" :rows="6" />
      </bg-field>
      <bg-field class="field" label="Start" v-if="test.platform != 'video'">
        <bg-input v-model="test.url" height="46px" />
      </bg-field>
      <bg-field class="field" label="Stop" v-if="test.platform != 'video'">
        <bg-input tag="textarea" :rows="3" v-model="test.stop_moment" />
      </bg-field>
      <bg-field class="field" label="Comment">
        <bg-input v-model="test.comments" height="46px" />
      </bg-field>
      <bg-field
        class="field"
        label="Brand name"
        v-if="test.platform === 'video'"
      >
        <bg-input v-model="test.brand_name" height="46px" />
      </bg-field>

      <bg-field class="field" label="Category">
        <bg-select
          @change="selectCategory"
          :options="categoryOptions"
          :value="test.category"
          label="name"
        ></bg-select>
      </bg-field>
      <bg-field class="field" label="Flow" v-if="test.platform != 'video'">
        <bg-select
          @change="selectFlow"
          :options="flowOptions"
          :value="test.flow"
          label="name"
        ></bg-select>
      </bg-field>

      <bg-field class="field" label="Requested by">
        <bg-select
          v-if="!$store.state.users.loading"
          @change="selectRequester"
          :options="$store.state.users.users"
          :value="test.requester"
          label="email"
          :clearable="true"
          :searchable="true"
        ></bg-select>
        <div v-else style="height: 46px">
          <div class="lds-ellipsis">
            <div></div>
            <div></div>
            <div></div>
            <div></div>
          </div>
        </div>
      </bg-field>
      <bg-field class="field" label="Responsible researcher">
        <bg-select
          v-if="!$store.state.users.loading"
          @change="selectConsultant"
          :options="consultantOptions"
          :value="test.researcher"
          label="email"
          :clearable="true"
          :searchable="true"
        ></bg-select>
        <div v-else style="height: 46px">
          <div class="lds-ellipsis">
            <div></div>
            <div></div>
            <div></div>
            <div></div>
          </div>
        </div>
      </bg-field>

      <bg-field class="field" label="Delivery date (YYYY-MM-DD)">
        <bg-input v-model="test.delivery_date" height="46px" />
      </bg-field>
    </div>
    <div
      class="testsetup"
      v-if="this.activeTab === 'methods' && !isLoading"
      :key="this.rerenderKey"
    >
      <bg-field class="field" label="Device">
        <bg-select
          @change="selectPlatform"
          :options="platformOptions"
          :value="test.platform"
          label="text"
        ></bg-select>
      </bg-field>
      <bg-field
        v-if="test.platform === 'mobile'"
        class="field"
        label="External app"
      >
        <bg-checkbox
          label="Test will be performed outside Brainpeek app"
          v-model="test.external_app"
        ></bg-checkbox>
      </bg-field>
      <bg-field class="field" label="Type of test">
        <bg-select
          @change="selectType"
          :options="typeOptions"
          :value="test.test_type"
          label="text"
        ></bg-select>
      </bg-field>
    </div>
    <div
      class="testsetup"
      v-if="this.activeTab === 'participants' && !isLoading"
      :key="this.rerenderKey"
    >
      <bg-field class="field" label="Target group">
        <bg-select
          @change="selectTarget"
          :options="targetOptions"
          :value="test.target_group"
          label="text"
        ></bg-select>
      </bg-field>
      <bg-field
        class="field"
        label="Target group requirements"
        v-if="this.test.target_group != 'default'"
      >
        <bg-input v-model="test.custom_target" tag="textarea" :rows="6" />
      </bg-field>
      <bg-field class="field" label="Minimum participants for metrics">
        <bg-input
          v-model="test.minimum_participants_for_metrics"
          type="number"
          height="46px"
        />
      </bg-field>
      <bg-field class="field" label="Automatic translation">
        <bg-select
          @change="selectTranslation"
          :options="translationOptions"
          :value="
            translationOptions.find((l) => l.name === test.translation_language)
          "
          label="nativeName"
          :searchable="true"
          :clearable="true"
        ></bg-select>
      </bg-field>
    </div>
    <div
      class="testsetup"
      v-if="this.activeTab === 'insights' && !isLoading"
      :key="this.rerenderKey"
    >
      <bg-field class="field" label="Purpose of test">
        <bg-input v-model="test.purpose" tag="textarea" :rows="12" />
      </bg-field>
      <bg-field class="field" label="Focus elements">
        <bg-input
          v-for="(point, index) in test.focus_elements"
          :key="index"
          v-model="test.focus_elements[index]"
          style="margin-bottom: 5px"
          height="46px"
        />
        <bg-button
          style="margin-top: 5px"
          variant="secondary"
          text="add focus element"
          @click="addFocus"
        ></bg-button>
      </bg-field>
      <bg-field
        class="field"
        label="Open questions (max 3)"
        v-if="test.platform === 'video'"
      >
        <bg-input
          v-for="(q, index) in openQuestions"
          :key="index"
          v-model="openQuestions[index].text"
          style="margin-bottom: 5px"
          height="46px"
          @change="editQuestions(q)"
        />
        <bg-input
          v-for="(k, index) in newQuestions"
          :key="index"
          v-model="newQuestions[index].text"
          height="46px"
          style="margin-bottom: 5px"
        >
        </bg-input>
        <bg-button
          style="margin-top: 5px"
          variant="secondary"
          text="add question"
          @click="addQuestion"
        ></bg-button>
      </bg-field>

      <bg-field
        class="field"
        label="Extra Associations (max 5)"
        v-if="test.platform === 'video'"
      >
        <bg-input
          v-for="(a, index) in extraAssociations"
          :key="index"
          v-model="extraAssociations[index].text"
          style="margin-bottom: 5px"
          height="46px"
          @change="editAssociations(a)"
        />
        <bg-input
          v-for="(b, index) in newAssociations"
          :key="index"
          v-model="newAssociations[index].text"
          height="46px"
          style="margin-bottom: 5px"
        >
        </bg-input>
        <bg-button
          style="margin-top: 5px"
          variant="secondary"
          text="add association"
          @click="addAssociation"
        ></bg-button>
      </bg-field>

      <addons-management
        v-if="test.platform === 'video'"
        :addons="test.addons"
        :handleAddonUpdateCallback="handleAddonUpdate"
        :possibleAddons="{
          tagon: 'Tagon (+2500)',
          individual_participant: 'Individual participant data (+900)',
        }"
      ></addons-management>
    </div>
    <bg-loader :dark="true" v-if="isLoading"></bg-loader>
    <bg-button v-if="!isLoading" text="Save" @click="updateTest()"></bg-button>
  </div>
</template>

<style lang="scss" scoped>
@import "./styles.scss";
</style>

<script>
import BgTypography from "@/directives/bg-typography";
import API from "@/api";
import BgField from "@/components/bg-field";
import BgInput from "@/components/bg-input";
import BgLoader from "@/components/bg-loader";
import BgSelect from "@/components/bg-select";
import BgButton from "@/components/bg-button";
import AddonsManagement from "@/components/addons-management";
import BgCheckbox from "@/components/bg-checkbox";
import languages from "./languages.json";
export default {
  components: {
    BgField,
    BgCheckbox,
    BgInput,
    BgLoader,
    BgSelect,
    BgButton,
    AddonsManagement,
  },
  directives: {
    BgTypography,
  },
  data() {
    return {
      activeTab: "setup",
      extraAssociations: [],
      changeMade: false,
      categoryOptions: [],
      companyOptions: [],
      flowOptions: [],
      isLoading: false,
      isLoadingUsers: true,
      isLoadingCompanies: true,
      isButtonLoading: false,
      openQuestions: [],
      newAssociations: [],
      newQuestions: [],
      platformOptions: ["Desktop", "Mobile"],
      rerenderKey: 0,
      typeOptions: ["Professional", "Essential"],
      test: {},
      statusOptions: ["done", "in-progress", "draft", "requested"],
      targetOptions: ["default", "custom"],
      translationOptions: languages,
    };
  },
  created() {
    this.fetchData();
  },
  computed: {
    emptyQuestions: function () {
      let empty = false;
      this.openQuestions.forEach((el) => {
        if (!el.text) {
          empty = true;
        }
      });
      this.extraAssociations.forEach((el) => {
        if (!el.text) {
          empty = true;
        }
      });
      return empty;
    },
    consultantOptions() {
      return this.$store.state.users.users.filter((u) => {
        return u.company.licence_analysis;
      });
    },
  },
  methods: {
    addQuestion() {
      this.newQuestions.push({
        text: "",
      });
    },
    addAssociation() {
      this.newAssociations.push({
        text: "",
      });
    },
    addFocus() {
      this.test.focus_elements.push("");
    },
    changeTab(val) {
      this.activeTab = val;
      this.rerenderKey = this.rerenderKey + 1;
    },
    async fetchData() {
      this.isLoading = true;
      this.test = {};
      this.test = await API.test.getTest(this.$route.params.id);

      const categories = await API.test.getCategories();
      if (categories) {
        categories.categories.forEach((el) => this.categoryOptions.push(el));
      }

      const flows = await API.test.getFlows();
      if (flows) {
        flows.flows.forEach((el) => this.flowOptions.push(el));
      }

      this.openQuestions = [];
      this.newQuestions = [];
      if (this.test.platform === "video") {
        this.test.open_questions.forEach((el) => {
          if (!el.default) {
            this.openQuestions.push(el);
          }
        });
        this.extraAssociations = [];
        this.newAssociations = [];
        this.test.associations.forEach((el) => {
          if (!el.default) {
            this.extraAssociations.push(el);
          }
        });
      }
      this.isLoading = false;
      this.isButtonLoading = false;
    },
    handleAddonUpdate(newAddonsData) {
      this.test.addons = newAddonsData;
    },
    editQuestions(q) {
      this.test.open_questions.find((el) => el.id === q.id).text = q.text;
    },
    editAssociations(a) {
      this.test.associations.find((el) => el.id === a.id).text = a.text;
    },
    selectPlatform(val) {
      this.test.platform = val.toLowerCase();
    },
    selectConsultant(val) {
      this.test.researcher_id = val.id;
    },
    selectCategory(val) {
      this.test.category_id = val.id;
    },
    selectFlow(val) {
      this.test.flow_id = val.id;
    },
    selectRequester(val) {
      this.test.requester_id = val.id;
    },
    selectCompany(val) {
      this.test.company_id = val.id;
    },
    selectType(val) {
      this.test.test_type = val.toLowerCase();
    },
    selectStatus(val) {
      this.test.state = val;
    },
    selectTarget(val) {
      this.test.target_group = val;
    },
    selectTranslation(val) {
      if (!val) {
        this.test.translation_language = null;
      } else {
        this.test.translation_language = val.name;
      }
    },
    async updateTest() {
      this.isButtonLoading = true;
      this.isLoading = true;
      if (this.newQuestions.length > 0 || this.newAssociations.length > 0) {
        this.newQuestions.forEach((el) => {
          if (el.text) {
            this.test.open_questions.push(el);
          }
        });
        this.newAssociations.forEach((el) => {
          if (el.text) {
            this.test.associations.push(el);
          }
        });
      }

      if (this.emptyQuestions) {
        this.extraAssociations.forEach((el) => {
          if (!el.text) {
            el._destroy = "1";
          }
        });
      }
      if (this.test.focus_elements && this.test.focus_elements.length > 0) {
        this.test.focus_elements.forEach((el, index) => {
          if (el === "") {
            this.test.focus_elements.splice(index, 1);
          }
        });
      }

      const res = await API.test.updateTest({ test: this.test });
      this.$store.dispatch("fetchAllTests");
      this.isLoading = false;
    },
  },
};
</script>
